import { ButtonLink, Card, Icon } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { locale } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import type { AfterStay } from '@/services/checkout'
import { placeName } from '../Accesos/shared'
import { dayRange, useLang } from '../Reserva/shared'
import '../Pagos/Pagos.css'
import './Cierre.css'

/** Rutas del bloque 7 (inventario D16; `/salida/despues` añadida en el bloque 7). */
export const CLOSE_ROUTES = {
  checkout: '/salida',
  after: '/salida/despues',
  notices: '/avisos',
  profile: '/perfil',
} as const

/** "Martes 6 de octubre" */
export function longDate(iso: string, lang: string): string {
  const text = new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso)).replace(',', '')
  return text.charAt(0).toLocaleUpperCase(lang) + text.slice(1)
}

/** "2 h 40 min" hasta la salida el mismo día; null si ya ha pasado o falta más de un día (la cabecera solo da la hora). */
export function remainingText(iso: string, now: Date, t: ReturnType<typeof useLang>['t']): string | null {
  const minutes = Math.round((new Date(iso).getTime() - now.getTime()) / 60000)
  if (minutes <= 0 || minutes > 24 * 60) return null
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? t('v2.estancia.cabecera.horasMin', { h, m }) : t('v2.estancia.cabecera.minutos', { m })
}

/** Acceso al Perfil desde la cabecera. */
export function AccountLinks() {
  const { t } = useLang()
  const { go, search } = useAppNavigate()
  const link = (to: string, icon: 'bell' | 'user', label: string) => (
    <a
      href={withQuery(to, search)}
      onClick={(e) => {
        e.preventDefault()
        go(to)
      }}
    >
      <Icon name={icon} size={20} />
      {label}
    </a>
  )
  return (
    <nav className="cierre__account" aria-label={t('v2.cuenta.perfil')}>
      {link(CLOSE_ROUTES.profile, 'user', t('v2.cuenta.perfil'))}
    </nav>
  )
}

/** Reseña en el canal donde se reservó (Cierre.dc.html post). Sin enlace del backend no se muestra (D36). */
export function ReviewCard({ review }: { review: AfterStay['review'] }) {
  const { t } = useLang()
  if (!review) return null
  const channel = review.channelName
  return (
    <Card as="section">
      <div className="cierre__card">
      <h2 id="cierre-resena" className="cierre__card-title">
        {t('v2.despues.resena.titulo')}
      </h2>
      {review.done ? (
        <p className="cierre__text">{t('v2.despues.resena.hecha', { channel })}</p>
      ) : (
        <>
          <p className="cierre__text">{t('v2.despues.resena.texto', { channel })}</p>
          <ButtonLink variant="secondary" href={review.url} target="_blank" rel="noreferrer">
            {t('v2.despues.resena.boton', { channel })}
          </ButtonLink>
        </>
      )}
      </div>
    </Card>
  )
}

/**
 * Volver a reservar (Cierre.dc.html post): enlace real al motor de reservas (`showGetNewReserve` + `environmentDataId`);
 * descuento por reserva directa y fechas sugeridas solo si los da el backend (mock). Las fechas son informativas: el
 * motor no recibe fechas por enlace.
 */
export function RebookCard({ rebook }: { rebook: AfterStay['rebook'] }) {
  const { t, lang } = useLang()
  const stay = useLoadedStay()
  if (!rebook) return null
  const discount = rebook.discountPercent
  return (
    <Card as="section" highlighted>
      <div className="cierre__card">
      <span className="cierre__caption">{discount ? t('v2.despues.volver.razonDescuento', { percent: discount }) : t('v2.estancia.despues.volver.razon')}</span>
      <h2 id="cierre-volver" className="cierre__card-title">
        {t('v2.despues.volver.titulo', { name: placeName(stay) })}
      </h2>
      {rebook.suggestedDates.length > 0 && (
        <ul className="cierre__dates tnum" aria-label={t('v2.despues.volver.fechas')}>
          {rebook.suggestedDates.map((d) => {
            const range = dayRange(d.from, d.to, lang)
            return (
              <li key={`${d.from}-${d.to}`} className="cierre__date">
                {d.label ? t('v2.despues.volver.fecha', { range, label: d.label }) : range}
              </li>
            )
          })}
        </ul>
      )}
      <ButtonLink href={rebook.url} target="_blank" rel="noreferrer">
        {t('v2.estancia.despues.volver.accion')}
      </ButtonLink>
      <p className="cierre__note">{discount ? t('v2.despues.volver.condicion') : t('v2.estancia.despues.volver.texto')}</p>
      </div>
    </Card>
  )
}

/** WhatsApp con la referencia y el mensaje de salida: aviso con la API actual (D52). */
export const leftWhatsappHref = (whatsapp: string, reference: string, t: ReturnType<typeof useLang>['t']) => whatsappHref(whatsapp, reference, t('v2.salida.mensajeSalida'))
